// @ts-strict-ignore
/**
 * CallService service
 * @module Calls
 */

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { CallDestination, CallMedia, CallRB, CallStatus, CallType, MediaType, Call } from "rainbow-web-sdk/models/call.model";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { ConversationServiceInnerEvents, ConversationServiceRB } from '../conversation/conversation.service';
import { WebrtcP2PService } from '../webrtc/webrtcP2P.service';
import { TelephonyService } from '../telephony/telephony.service';
import { WebrtcGatewayService } from '../webrtcGateway/webrtcGateway.service';
import type { BubbleServiceRB } from '../bubbles/bubble.service';
import { BUBBLE_SVC } from '../bubbles/bubble.service';
import type { BubbleConferenceServiceRB } from '../conference/bubbleConference.service';
import { BUBBLE_CONFERENCE_SVC } from '../conference/bubbleConference.service';
import { Bubble, BubbleEvents, Conversation, RBEvent, User } from 'rainbow-web-sdk/models';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ConversationRB } from 'rainbow-web-sdk/models/conversation.model';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { filter } from 'rxjs/operators';
import { JingleSession } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { WebrtcConfigAndUtils } from '../webrtc/webrtcConfigAndUtils';
import xmlParser from '../../utils/xmlParser';
import { MainService } from '../core';
import { $msg } from 'strophe.js';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { TelephonyCCDAgentService } from '../telephony/telephonyCcdAgent.service';

/**
 * @internal
 */
export const CALL_SVC = 'CallService';

export interface PhoneCallAdditionalData {
    /**
     * @internal
     * The subject of the call (The subject is shown only if the distant user is using Rainbow)
     */
    callSubject?: string;

    /**
     * This allows to transport custom data through the PBX / CSTA between the caller and the callee
     */
    correlatorData?: string;

    /**
     * The same as correlatorData but allows to use non-editable chars
     */
    correlatorDataHex?: string;

    /**
     * @internal
     * In case a user wants to dial a number from its call log which contains a calledNumber, this parameter has to be used.
     * It is then used in priority to initiate the make call.
     * ONLY USED if USER is parameter is present
     */
    calledNumber?: string;
}

/**
 * Call Service interface
 */
export interface CallService {
    /**
     * Subscribe to updates from the call service, all events are of RBEvent<CallServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - Array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<CallServiceEvents>) => any, eventNames?: CallServiceEvents | CallServiceEvents[]): Subscription;

    /**
     * Make a standard WebRTC call toward a Rainbow user.
     * A new conversation with the user will be created if not existing already.
     * @param user - The user we want to call
     * @param medias - Array containing the media to be used when setting up the new call
     * By default, only the AUDIO media type will be added to the new call.
     * @param message - A complementary message send to the remote party informing about the reason of the call
     */
    makeWebCall(user: User, medias?: MediaType[], message?: string): Promise<void>;

    /**
     * Make a PBX Phone call towards a number
     * A new conversation will be created if not existing already (even if it's with an external number)
     * @param number - The number we want to call; The format could be anything the user can type, it will be transformed in E164 format.
     * @param user - A user to whom this number belongs to (in case of Rainbow User)
     * @param additionalData - An additional data that can be send during the make call
     */
    makePhoneCall(number: string, user?: User, additionalData?: PhoneCallAdditionalData): Promise<void>;

    /**
     * Find and return the current active call (the last one found), or null otherwise.
     * This method retrieves the current call that is in an active state.
     * @returns The current active call object if one exists, or null if no active call is found
     */
    getActiveCall(): Call | null;

}

/**
 * @internal
 * Call service events send to other services
 * Can listen to it via `CallService.subscribe()` API.
 */
export enum CallServiceEvents {
    /**
     * @eventProperty
     * This RBEvent is sent when a call is accepted
     *
     * **Event Data**
     * - **call**: The call that is accepted
     */
    ON_CALL_ACCEPTED = 'ON_CALL_ACCEPTED',

    /**
     * @eventProperty
     * An audio call only has been requested
     */
    ON_MAKE_AUDIO_CALL = 'ON_MAKE_AUDIO_CALL',

    /**
     * @eventProperty
     * A phone call has been requested
     */
    ON_MAKE_PHONE_CALL = 'ON_MAKE_PHONE_CALL',

    /**
     * @eventProperty
     * The file of the recording of the call is ready to be downloaded
     */
    ON_CALL_FILE_RECORDER_READY = 'ON_CALL_FILE_RECORDER_READY',

    /**
     * @eventProperty
     * The file descriptor of the recording of the call is ready
     */
    ON_CALL_RECORDER_FILE_DESCRIPTOR_READY = "ON_CALL_RECORDER_FILE_DESCRIPTOR_READY",
}

/**
 * @internal
 */
export class CallServiceRB extends Service implements CallService {

    private webrtcP2PService: WebrtcP2PService = WebrtcP2PService.getInstance();
    private telephonyService: TelephonyService = TelephonyService.getInstance();
    private webrtcGatewayService: WebrtcGatewayService = WebrtcGatewayService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private bubbleService: BubbleServiceRB;
    private webConferenceService: BubbleConferenceServiceRB;
    private contactService: ContactService = ContactService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private mainService: MainService = MainService.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private ccdAgentService: TelephonyCCDAgentService = TelephonyCCDAgentService.getInstance();
    //private callRecorderService: CallRecorderService = CallRecorderService.getInstance();

    //only used for UCAAS
    private rxSubject: Subject<RBEvent> = new Subject<RBEvent>();
    private jingleSubscription: Subscription;
    private xmppMessageHandler;

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------

    public static getInstance() { return Service.create(CALL_SVC, CallServiceRB); }
    public static build() { return new CallServiceRB(); }
    private constructor() {
        super();

        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); });
        if (this.xmppService.started) {
            this.attachHandlers();
        }
    }

    public async start(): Promise<void> {
        this.logger.info("[CallService] === STARTED ===");
    }

    public async stop(): Promise<void> {
        if (this.xmppMessageHandler) { this.xmppService.deleteHandler(this.xmppMessageHandler); this.xmppMessageHandler = null; }
        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = null; }
        this.logger.info("[CallService] === STOPPED ===");
    }

    private onConnectionStateChangeEvent(event) {
        if (event.name === 'ON_XMPP_CONNECTED' && event.data?.reason === 'PRESENCE') {
            this.logger.log('[CallService] onConnectionStateChangeEvent : connected');
            this.attachHandlers();
        }
        else if (event.name === 'ON_XMPP_DISCONNECTED' && event.data?.reason === 'LOGOUT') {
            //logout, stop service
            this.stop();
        }
    }

    private attachHandlers() {
        if (!this.bubbleService) { this.bubbleService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC); }
        if (!this.webConferenceService) { this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC); }

        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = null; }
        this.jingleSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((evt: RBEvent) => { this.onJingleMessage(evt); });

        if (this.xmppMessageHandler) {
            this.xmppService.deleteHandler(this.xmppMessageHandler);
            this.xmppMessageHandler = null;
        }
        this.xmppMessageHandler = this.xmppService.addHandler((stanza) => {
            try {
                this.onMessageReceived(stanza); return true;
            } catch (err) {
                this.logger.error(`[CallService] caught error in handler ${err}`);
                return true;
            }
        }, "urn:xmpp:jingle:apps:rtp:info:1", "message");

    }

    private onJingleMessage(event: RBEvent): void {
        try {
            switch (event.name) {
                case 'jingleP2PUpdateStream': this.jingleP2PUpdateStream(event.data.sid); break;
                case 'setLocalDescription.jingle': this.jingleP2POnSetLocalDescription(event.data.sid); break;
                case 'jingleSessionInfoRefer': this.onJingleSessionRefer(event.data.sid, event.data.status, event.data.confEndpointId, event.data.referTo, event.data.method, event.data.otherSid); break;
                default:
                    break;
            }
        }
        catch (error) {
            this.logger.info(`[CallService] onJingleMessage -- failure -- ${event.name}`);
        }
    }

    /**
     * Subscribe to updates from the call service, all events are of RBEvent<CallServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<CallServiceEvents>) => any, eventNames?: CallServiceEvents | CallServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<CallServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<CallServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[ConversationService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: CallServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    // ------------------------------------------------------------
    //                      PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    /**
     * Find and return the current active call (the last one found), or null otherwise.
     * This method retrieves the current call that is in an active state.
     * @returns The current active call object if one exists, or null if no active call is found
     */
    public getActiveCall(): Call | null {
        let call = this.webrtcP2PService.getCurrentActiveCall();
        if (!call) call = this.telephonyService.getActiveCall();
        return call;
    }

    /**
     * Make a PBX Phone call towards a number
     * A new conversation will be created if not existing already (even if it's with an external number)
     * @param number - The number we want to call; The format could be anything the user can type, it will be transformed in E164 format.
     * @param user - A user to whom this number belongs to (in case of Rainbow User)
     * @param additionalData - An additional data that can be send during the make call
     */
    public async makePhoneCall(number: string, user?: User, additionalData?: PhoneCallAdditionalData): Promise<void> {
        try {
            if (!number) { throw new Error("missing number to call"); }
            if (this.contactService.userContact.isGuest()) { throw new Error("bubble guest user not allowed to make call"); }

            this.sendEvent(CallServiceEvents.ON_MAKE_PHONE_CALL);

            if (!user) {
                this.logger.info(`[CallService] makePhoneCall to ${anonymizer(number)}`);
                await this.telephonyService.makeCallByPhoneNumber(number, additionalData?.callSubject, additionalData?.correlatorData, additionalData?.correlatorDataHex);
            }
            else {
                this.logger.info(`[CallService] makePhoneCall to ${anonymizer(number)} related to user ${user?.id}`);
                await this.telephonyService.makeCall(user as Contact, number, additionalData?.callSubject, additionalData?.correlatorData, additionalData?.correlatorDataHex, additionalData?.calledNumber);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] makePhoneCall error to ${number} and related user ${user?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Make a standard WebRTC call toward a Rainbow user.
     * A new conversation with the user will be created if not existing already.
     * @param user - The user we want to call
     * @param medias - Array containing the media to be used when setting up the new call
     * By default, only the AUDIO media type will be added to the new call.
     * @param message - A complementary message send to the remote party informing about the reason of the call
     */
    public async makeWebCall(user: User, medias: MediaType[] = [MediaType.AUDIO], message = ""): Promise<void> {
        try {
            if (!user) { throw new Error("missing user object"); }
            //if (this.contactService.userContact.isGuest()) { throw new Error("bubble guest user not allowed to make call"); }

            let managedMediaType: string = "audio";

            if (!medias.includes(MediaType.AUDIO)) {
                // no audio
                if (medias.includes(MediaType.VIDEO)) {
                    managedMediaType = "videoOnly";
                }
                if (medias.includes(MediaType.SHARING)) {
                    managedMediaType = "sharingOnly";
                }
            }
            else {
                if (medias.includes(MediaType.VIDEO)) {
                    managedMediaType = "video";
                }
                if (medias.includes(MediaType.SHARING)) {
                    managedMediaType = "sharing";
                }
                if (medias.includes(MediaType.VIDEO) && medias.includes(MediaType.SHARING)) {
                    managedMediaType = "videoSharing";
                }
            }
            this.logger.info(`[CallService] makeWebCall ${user.id} ${managedMediaType}`);

            // First activate telephony app view (for softphone setting)
            if (managedMediaType === 'audio') this.sendEvent(CallServiceEvents.ON_MAKE_AUDIO_CALL);

            await this.webrtcP2PService.makeCall(user as Contact, managedMediaType, message);
        }
        catch (error) {
            this.logger.error(`[CallService] makeWebCall error ${user?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Answer the call specified by its object.
     * This feature can be used only if the "answer" call capability is 'True'.
     * By default, the answer is done with AUDIO only.
     * If the 'withVideo' option is set to 'True' and the incoming call contains video, the answer will directly be done with audio and video.
     * @param call - The Call object that we want to answer
     * @param withVideo - Option to answer the call directly with audio and video. Default value is 'False'
     */
    public async answerCall(call: CallRB, withVideo: boolean = false): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.answer) { throw new Error(`call cannot be answerred`); }

            this.logger.info(`[CallService] answerCall ${call?.id}`);

            if (call.callType === CallType.WEBRTC) {
                if (withVideo && this.contactService.userContact.webrtcVideoEnabled && (call.remoteMedia & CallMedia.VIDEO)) {
                    //answer with video
                    this.webrtcP2PService.answerCall(call, "video");
                }
                else this.webrtcP2PService.answerCall(call);
            }
            else {
                if (this.webrtcGatewayService.isMediaPillarCallSituation(call)) {
                    await this.webrtcGatewayService.answerCall(call);
                }
                else {
                    await this.telephonyService.answerCall(call);
                }
            }

            if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
                //modify the state to answering, as the call has been answerred successfully via API or GW
                //connecting is used for reconnection too, so it's not a good state to be used... answering is better
                //RAYMOND IS THIS A SAFE THING ??
                //as OXE will change the call (for multiset user), this one will be removed and another call should appear... we need to see if we to ignore OXE
                if (call.makecallCtiExtern && call.contact?.temp) {
                    call.contact.displayName = this.i18n.translate("launchInProgress");
                }
                call.setStatus(CallStatus.ANSWERING);
                this.conversationService.updateCallCapabilities(call);
            }

            this.sendEvent(CallServiceEvents.ON_CALL_ACCEPTED, { call });
        }
        catch (error) {
            this.logger.error(`[CallService] answerCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Release the call specified by its object.
     * This method allows to decline an incoming call or stop an outgoing call.
     * This feature can be used only if the "release" call capability is 'True'.
     * @param call - The Call object that we want to decline or release.
     * @param reason - Reason for releasing the call (only for Web calls)
     */
    public async releaseCall(call: CallRB, reason: string = ""): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.release) { throw new Error(`call cannot be released`); }

            this.logger.info(`[CallService] releaseCall ${call?.id}`);

            if (call.callType === CallType.WEBRTC) {
                this.webrtcP2PService.terminateCall(call, reason);
            }
            else {
                if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
                    await this.telephonyService.declineCall(call);
                }
                else {
                    await this.telephonyService.releaseCall(call);
                }
            }
        }
        catch (error) {
            this.logger.error(`[CallService] releaseCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Deflect the incoming call to the voice mail.
     * This feature can be used only if the "deflectToVoicemail" call capability is 'True'.
     * The 'deflect to voice mail' is only available on HYBRID telephony systems.
     * For Cloud telephony systems, the release call will follow the user configuration (which could be deflect or other).
     * @param call - The Call object that we want to deflect to the voice mail.
     */
    public async deflectToVoicemail(call: CallRB): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.deflectToVoicemail) { throw new Error(`call cannot be deflected to VM`); }

            this.logger.info(`[CallService] deflectToVoicemail ${call?.id}`);

            await this.telephonyService.deflectCallToVM(call); return;
        }
        catch (error) {
            this.logger.error(`[CallService] deflectToVoicemail error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * @public
     * Deflect (redirect) the current incoming call to the specified phone number.
     * This feature can be used only if the "deflectToPhoneNumber" call capability  is 'True'.
     * The list of number where we can deflect the incoming call is available in the 'deflectPhoneNumberDestinations' call capability.
     * @param call - The Call object that we want to deflect.
     * @param number - The phone number the call will be deflected to.
     */
    public async deflectToPhoneNumber(call: CallRB, number: string): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.deflectToPhoneNumber) { throw new Error(`call cannot be deflected to phone number`); }

            this.logger.info(`[CallService] deflectToPhoneNumber ${call?.id}`);

            //perhaps we need to check the number, but for the moment leave the door open...

            await this.telephonyService.deflectCallToNumber(call, number);
        }
        catch (error) {
            this.logger.error(`[CallService] deflectToPhoneNumber error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Mute/Unmute the call specified by its object.
     * This feature can be used only if the "mute"/"unmute" call capability is 'True'.
     * @param call - The call
     * @param state - if true, the audio in the call will be muted
     */
    public muteCall(call: CallRB, state: boolean): void {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.mute && !call.capabilities.unmute) { throw new Error(`call cannot be muted/unmuted`); }

            if (state) { this.logger.info(`[CallService] muteCall ${call?.id}`); }
            else { this.logger.info(`[CallService] un-muteCall ${call?.id}`); }

            if (call.callType === CallType.WEBRTC) {
                this.webrtcP2PService.muteCall(call, state);
            }
            else {
                this.webrtcGatewayService.muteAudio(call, state, null);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] muteCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Add a media to the call specified by its object.
     * This feature can be used only for WebRTC Calls.
     * It is checked that the specified media can be added according to the associated call capability, i.e.
     *  - The "addAudio" call capability for "AUDIO" media
     *  - The "addVideo" call capability for "VIDEO" media
     *  - The "addSharing" call capability for "SHARING" media
     * Check the call capabilities (addAudio, addVideo, addSharing)
     * @param call - The call object
     * @param mediaType - The type of media to add to the call.
     */
    public async addMediaToCall(call: CallRB, media: MediaType): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }

            this.logger.info(`[CallService] addMediaToCall ${call?.id} -- ${media}`);
            switch (media) {
                case MediaType.AUDIO: {
                    if (!call.capabilities.addAudio) {
                        throw new Error(`cannot add audio to call`);
                    }
                    await this.webrtcP2PService.addAudioToCall(call);
                    break;
                }
                case MediaType.VIDEO: {
                    if (!call.capabilities.addVideo) {
                        throw new Error(`cannot add video to call`);
                    }
                    await this.webrtcP2PService.addVideoToCall(call);
                    break;
                }
                case MediaType.SHARING: {
                    if (!call.capabilities.addSharing) {
                        throw new Error(`cannot add sharing to call`);
                    }
                    await this.webrtcP2PService.addSharingToCall(call, true);
                    break;
                }
                default: break;
            }
        }
        catch (error) {
            this.logger.error(`[CallService] addMediaToCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Remove a media from the call specified by its object.
     * This feature can be used only for WebRTC Calls.
     * It is checked that the specified media can be removed according to the associated call capability, i.e.
     *  - The "removeVideo" call capability for "VIDEO" media
     *  - The "removeSharing" call capability for "SHARING" media
     * @param call - The call object
     * @param mediaType - The type of media to remove from the call.
     */
    public async removeMediaFromCall(call: CallRB, media: MediaType): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }

            this.logger.info(`[CallService] removeMediaFromCall ${call?.id} -- ${media}`);
            switch (media) {
                case MediaType.VIDEO: {
                    if (!call.capabilities.removeVideo) {
                        throw new Error(`cannot remove video to call`);
                    }
                    await this.webrtcP2PService.removeVideoFromCall(call);
                    break;
                }
                case MediaType.SHARING: {
                    if (!call.capabilities.removeSharing) {
                        throw new Error(`cannot add sharing to call`);
                    }
                    await this.webrtcP2PService.removeSharingFromCall(call);
                    break;
                }
                default: break;
            }
        }
        catch (error) {
            this.logger.error(`[CallService] removeMediaFromCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Put on hold the call specified by its object.
     * This feature can be used only if the "hold" call capability is 'True'.
     * @param call - The call object
     */
    public async holdCall(call: CallRB): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.hold) { throw new Error(`call cannot be hold`); }

            this.logger.info(`[CallService] holdCall ${call?.id}`);

            if (call.callType === CallType.WEBRTC) {
                this.webrtcP2PService.holdCall(call, true);
            }
            else {
                await this.telephonyService.holdCall(call);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] holdCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Retrieve from hold the call specified by its object.
     * This feature can be used only if the "retrieve" call capability is 'True'.
     * @param call - The call object
     */
    public async retrieveCall(call: CallRB): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.retrieve) { throw new Error(`call cannot be retrieved`); }

            this.logger.info(`[CallService] retrieveCall ${call?.id}`);

            if (call.callType === CallType.WEBRTC) {
                this.webrtcP2PService.holdCall(call, false);
            }
            else {
                //check if CCD case
                const isCCDAgent: boolean = this.ccdAgentService.isCcdAgentLogged();
                if (isCCDAgent) {
                    // Alternate between active and hold call; if no active call, then do a standard retrieve
                    const activeCall: CallRB = this.telephonyService.getActiveCall();
                    if (activeCall) {
                        await this.telephonyService.alternateCall(activeCall, call);
                        return;
                    }
                }

                await this.telephonyService.retrieveCall(call);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] retrieveCall error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    private mergeCallsInProgress: boolean = false;
    /**
     * Merge the current 2 calls into one single.
     * This feature can be used only if the "mergeCalls" call capability is 'True'.
     * In case of standard HYBRID conference, the return value is null. A new telephony conference (conversation with "conference" call) will be created.
     * Otherwise, the result will be a web conference (in the form of a Rainbow bubble) that will be created in a new bubble conversation.
     * @returns Bubble Conversation with active web conference or null in case of hybrid 3-party conference call.
     */
    public async mergeCalls(): Promise<ConversationRB | undefined> {

        if (this.mergeCallsInProgress) {
            this.logger.warn("[callService] mergeCalls failure - mergeCalls already in progress");
            throw new Error(`mergeCalls failure - mergeCalls already in progress`);
        }

        try {
            let conversation: ConversationRB | undefined;
            this.mergeCallsInProgress = true;

            if (!this.bubbleService) throw new Error(`[CallService] mergeCalls -- bubble plugin is missing`);

            //check if this is ad-hoc conference
            const calls: CallRB[] = this.webrtcP2PService.getCurrentCallsNotMediaPillar();
            if (calls.length > 1) {

                if (!this.webConferenceService) throw new Error(`[CallService] mergeCalls -- bubble conference plugin is missing`);

                //get active call
                let call: CallRB = this.webrtcP2PService.getActiveWebrtcAudioCall();
                if (!call) {
                    //both calls are on hold, take the first one
                    call = calls[0];
                }

                //check capability for call
                if (!call.capabilities.mergeCalls) { throw new Error(`calls cannot be merged`); }

                const caller: any = { userId: this.contactService.userContact.dbId };
                const participantInfo1: any = this.bubbleService.getParticipantDataForAdhocFromCall(calls[0]);
                const participantInfo2: any = this.bubbleService.getParticipantDataForAdhocFromCall(calls[1]);
                const participants: any = [participantInfo1.participant, participantInfo2.participant];

                const adhocData: any = await this.bubbleService.startAdHocConference(caller, participants);
                this.logger.info(`[CallService] mergeCalls -- ad hoc started ${JSON.stringify(adhocData)}`);
                let bubble: Bubble = await this.bubbleService.getBubble(adhocData.roomId);
                // Workaround if the Bubble is not yet updated on the server, asks for a refresh
                if (!bubble?.isBubbleConferenceStarted()) {
                    bubble = await this.bubbleService.getServerBubble(adhocData.roomId);
                }
                const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
                const isMuted: boolean = call.isMuted;

                await this.webConferenceService.transferP2pCallToConference(session, adhocData.jingleJid, bubble, true, isMuted);
                conversation = await this.conversationService.getOrCreateConversation(bubble.jid, 'room');

                this.webrtcP2PService.referTo(session, bubble.dbId, adhocData.jingleJid);

                //send refer to the other session
                const secondSessionId: string = (call.id === calls[0].id) ? calls[1].id : calls[0].id;
                const secondSession: JingleSession = this.xmppService.connection.jingle.sessions[secondSessionId];

                this.webrtcP2PService.referTo(secondSession, bubble.dbId, adhocData.jingleJid);
            }
            else if (calls.length === 1) {

                //we've a mix of webrtc and PSTN conf call (sip wise case)
                const callOnHold: CallRB = this.telephonyService.getCallsOnHold()[0];
                const activeCall: CallRB = this.telephonyService.getActiveCall();

                if ((!callOnHold && !activeCall) || !calls[0].capabilities.mergeCalls) { throw new Error(`calls cannot be merged`); }

                let result: any;
                if (callOnHold) {
                    this.logger.info(`[CallService] mergeCalls -- mix webrtc pstn case ${calls[0].id} ${callOnHold.id}`);
                    result = await this.telephonyService.conferenceCall(calls[0], callOnHold);
                }
                else if (activeCall) {
                    this.logger.info(`[CallService] mergeCalls -- mix webrtc pstn case ${activeCall.id} ${calls[0].id}`);
                    result = await this.telephonyService.conferenceCall(activeCall, calls[0]);
                }

                if (result) {
                    conversation = await this.conversationService.getOrCreateConversation(result["jid"], 'room');
                }
            }
            else {

                //no webrtc calls, do a standard conference pbx (sipwise or hybride)
                const callOnHold: CallRB = this.telephonyService.getCallsOnHold()[0];
                const activeCall: CallRB = this.telephonyService.getActiveCall();

                if (!callOnHold || !activeCall || !activeCall?.capabilities.mergeCalls) { throw new Error(`calls cannot be merged`); }

                this.logger.info(`[CallService] mergeCalls -- 2 pbx calls ${activeCall.id} ${callOnHold.id} `);

                const result: any = await this.telephonyService.conferenceCall(activeCall, callOnHold);
                if (result) {
                    conversation = await this.conversationService.getOrCreateConversation(result["jid"], 'room');
                }
            }

            this.mergeCallsInProgress = false;
            return conversation;
        }
        catch (error) {
            this.logger.error(`[CallService] mergeCalls error -- ${error?.message} ${error?.errorDetailsCode}`);
            this.mergeCallsInProgress = false;
            throw error;
        }
    }

    /**
     * Add participants to the the current call.
     * The participants could be other Rainbow users or phone numbers (if the PBX system supports it).
     * This will result in a new conversation being created with web conference inside. The active call will be "transferred" to the bubble conference;
     * @param participants - The list of participants to be added to the call
     * @returns The new bubble conversation where the web conference is active
     */
    public async addParticipantsToCall(call: CallRB, participants: CallDestination[] = []): Promise<Conversation> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!participants.length) { throw new Error("missing participants"); }
            if (!call.capabilities.addParticipantsToCall) { throw new Error(`cannot add participants to call`); }

            if (!this.bubbleService) throw new Error(`[CallService] addParticipantsToCall to call ${call?.id} -- bubble plugin is missing`);
            if (!this.webConferenceService) throw new Error(`[CallService] addParticipantsToCall -- bubble conference plugin is missing`);

            this.logger.info(`[CallService] addParticipantsToCall to call ${call?.id}`);

            // Adapt the array of call recipients to the format expected by method 'startAdHocConference'
            // To do this, if present, the 'user' data (as User model) will be replaced to keep only the dbId of each specified user.
            const participantsList: any = participants.map((item) => {
                return { userId: item?.user?.dbId, phoneNumber: item.phoneNumber, country: item.country };
            });

            const caller: any = { userId: this.contactService.userContact.dbId };//provision for caller obj when not myself userId
            const participantInfo: any = this.bubbleService.getParticipantDataForAdhocFromCall(call);
            participantsList.unshift(participantInfo.participant);

            const adhocData: any = await this.bubbleService.startAdHocConference(caller, participantsList);
            this.logger.info(`[CallService] addParticipantsToCall -- ad hoc started ${JSON.stringify(adhocData)}`);
            let bubble: Bubble = await this.bubbleService.getBubble(adhocData.roomId);
            //workaround if the room is not yet updated on the server, asks for a refresh
            if (!bubble?.isBubbleConferenceStarted()) {
                bubble = await this.bubbleService.getServerBubble(adhocData.roomId);
            }

            let session: any = null;
            let isMuted: boolean = false;
            if (call.callType === CallType.WEBRTC) {
                session = this.xmppService.connection.jingle.sessions[call.id];
                isMuted = call.isMuted;
            }

            await this.webConferenceService.transferP2pCallToConference(session, adhocData.jingleJid, bubble, true, isMuted);
            const conversation: ConversationRB = await this.conversationService.getOrCreateConversation(bubble.jid, 'room');

            if (participantInfo.blindTrasfer) {
                const activeCallBlindTransferData: any = (adhocData?.gatewayPrefix && adhocData.participantGatewaySessionIds) ? adhocData.gatewayPrefix + adhocData.participantGatewaySessionIds[0] : "";
                this.logger.info(`[CallService] addParticipantsToCall -- activeCallBlindTransfer ${activeCallBlindTransferData}`);
                this.telephonyService.blindTransfertCall(call, activeCallBlindTransferData);
            }
            else if (call.callType === CallType.PHONE) {
                this.logger.info(`[CallService] addParticipantsToCall -- activeCall invite via IQ refer`);

                const id: string = "web_" + crypto.randomUUID();
                //Ask to callee to join SFU but not with IQ
                this.xmppService.send($msg({ from: this.contactService.userContact.fullJid, to: call.contact.jid, id })
                    .c('refer', {
                        xmlns: 'urn:xmpp:jingle:apps:rtp:info:1',
                        confEndpointId: bubble.dbId,
                        referTo: adhocData.jingleJid,
                        telCallId: call.id
                    }));

            } else {
                //full webrtc P2P situation
                this.webrtcP2PService.referTo(session, bubble.dbId, adhocData.jingleJid);
            }

            return conversation;
        }
        catch (error) {
            this.logger.error(`[CallService] addParticipantsToCall error -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Blind transfer the current call another destination which could be any rainbow user or phone number.
     * A blind transfer is when you transfer a call directly to another party without consulting with the person receiving the call.
     * In other words, there is no need to call the other party to make the transfer.
     * This feature is available only if the connected user is behind a Rainbow Hub telephone system.
     * @param call - The call to be transferred
     * @param destination - The destination of the blind transfer
     */
    public async blindTransfer(call: CallRB, destination: CallDestination): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!destination || (!destination.user && !destination.phoneNumber)) { throw new Error("missing destination"); }
            if (!call.capabilities.blindTransfer) { throw new Error(`cannot blindTransfer the call`); }

            this.logger.info(`[CallService] blindTransfer call ${call?.id} to ${destination.phoneNumber} ${destination?.user?.dbId}`);

            if (call.callType === CallType.PHONE) {
                if (!destination.phoneNumber) { throw new Error("missing phoneNumber destination"); }
                await this.telephonyService.blindTransfertCall(call, destination.phoneNumber);
            }
            else {
                const result: any = await this.telephonyService.telephonyServiceSipWise.transferRouting(call.contact.dbId, destination?.user?.dbId, destination.phoneNumber);
                if (result && result.error) {
                    throw result.error;
                }

                let method: string = "";
                let referTo: string = "";
                if (result.jid_im) {
                    method = "webrtc";
                    referTo = result.jid_im;
                } else if (result.phoneNumber) {
                    method = "pbx";
                    referTo = result.phoneNumber;
                }
                this.webrtcP2PService.sendReferTransfer(call.id, method, referTo);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] blindTransfer error -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Transfer the current call to another call.
     * If the destination call is not specified, then the transfer will be done towards the current active call.
     * The list of destination calls to which we can deflect the call current is available in the 'transferCallDestinations' call capability.
     * @param call - The call to be transferred
     * @param destinationCall - The destination call to which we want to transfer the current call.
     */
    public async transferToOtherCall(call: CallRB, destinationCall?: CallRB): Promise<void> {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.transferToOtherCall || !call.capabilities.transferCallDestinations.length) { throw new Error(`cannot transfer the call`); }

            //if no destination, take the first one from the list
            if (!destinationCall) {
                destinationCall = call.capabilities.transferCallDestinations[0] as CallRB;
            }
            else {
                //check if the destination call is acceptable
                let isAcceptableDestination: boolean = false;
                call.capabilities.transferCallDestinations.forEach((destination: CallRB) => {
                    if (destinationCall.id === destination.id) { isAcceptableDestination = true; }
                });

                if (!isAcceptableDestination) { throw new Error("destination not acceptable"); }
            }

            this.logger.info(`[CallService] transferToOtherCall call ${call?.id} to ${destinationCall.id}`);

            //make the transfer
            //main call is PSTN
            if (call.callType === CallType.PHONE) {
                if (destinationCall.callType === CallType.WEBRTC) {
                    //pstn + webrtc
                    const destionationObject: any = {
                        userId: destinationCall.contact.dbId,
                        resource: this.xmppService.getResourceFromJid(destinationCall.fullJid)
                    };
                    await this.telephonyService.blindTransfertCall(call, null, destionationObject);
                    this.webrtcP2PService.terminateCall(destinationCall);

                }
                else {
                    //both calls are PSTN
                    if (call.callStatus === CallStatus.ACTIVE || call.callStatus === CallStatus.RINGING_OUTGOING) { await this.telephonyService.transfertCall(call, destinationCall); }
                    else { await this.telephonyService.transfertCall(destinationCall, call); }
                }
            }
            //main call is WEBRTC
            else {
                if (destinationCall.callType === CallType.WEBRTC) {
                    //webrtc to webrtc
                    this.logger.info(`[p2pCallAreaComponent] transferPstnToWebrtc -- transfer webrtc to webrtc case`);
                    //send refer to the other session
                    const session: any = this.xmppService.connection.jingle.sessions[call.id];
                    const secondSession: any = this.xmppService.connection.jingle.sessions[destinationCall.id];

                    if (call.callStatus === CallStatus.HOLD) { this.webrtcP2PService.transferCall(session, secondSession); }
                    else { this.webrtcP2PService.transferCall(secondSession, session); }
                }
                else {
                    //pstn + webrtc
                    const destionationObject: any = {
                        userId: call.contact.dbId,
                        resource: this.xmppService.getResourceFromJid(call.fullJid)
                    };
                    await this.telephonyService.blindTransfertCall(destinationCall, null, destionationObject);
                    this.webrtcP2PService.terminateCall(call);
                }
            }

        }
        catch (error) {
            this.logger.error(`[CallService] transferToOtherCall error -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    /**
     * Send DTMF signal to the call specified by its object.
     * This feature can be used only if the "dtmf" call capability is 'True'.
     * @param call - The call object
     * @param dialKey - the DTMF character sequences to be send
     * Only the following characters are authorised: the ten single digits (0..9), the letters (a,b,c,d), the asterisk (*) and the pound sign (#).
     */
    public sendDTMF(call: CallRB, dialKey: string): void {
        try {
            if (!call) { throw new Error("missing call object"); }
            if (!call.capabilities.dtmf) { throw new Error(`call cannot send DTMF`); }

            this.logger.info(`[CallService] sendDTMF ${call?.id} ${dialKey}`);

            if (this.webrtcGatewayService.isMediaPillarCallCase()) {
                const mediaPillarCallContext: any = this.webrtcGatewayService.getMediaPillarCallContext(call);
                if (mediaPillarCallContext) {
                    const webrtcCallRef: any = mediaPillarCallContext.webrtcCallRef;
                    if (webrtcCallRef && webrtcCallRef.callStatus === CallStatus.ACTIVE) {
                        this.webrtcP2PService.sendDTMF(webrtcCallRef, dialKey);
                    }
                }
            } else {
                this.telephonyService.sendDtmf(call, dialKey);
            }
        }
        catch (error) {
            this.logger.error(`[CallService] sendDTMF error ${call?.id} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
    }

    public attachStreamToHtmlElement(call: CallRB, elementId: string, mediaType: number, local: boolean = false) {
        const session: any = this.xmppService.connection.jingle.sessions[call.id];
        let mediaStream;
        //find the right stream to add
        if (local) {
            call.localStreams.forEach((stream: MediaStream) => {
                if (stream.getVideoTracks().length && stream.getAudioTracks().length && mediaType === CallMedia.VIDEO) {
                    mediaStream = stream;
                }
                //if sharing track
                else if (stream.getVideoTracks().length && !stream.getAudioTracks().length && mediaType === CallMedia.SHARING) {
                    mediaStream = stream;
                }
            });
        }
        else {
            if (mediaType === CallMedia.VIDEO) { mediaStream = session?.remoteStreamsObject["1"]; }
            if (mediaType === CallMedia.SHARING) { mediaStream = session?.remoteStreamsObject["2"]; }
        }

        const element = document.getElementById(elementId);
        if (!element || !mediaStream) {
            throw new Error(`[CallService] attachStreamToHtmlElement -- missing element or media stream not found`);
        }

        this.webrtcConfigAndUtils.RTC.attachMediaStreamIfNeeded([element], mediaStream, mediaStream.id);
    }

    public attachStreamToExternalHtmlElement(call: CallRB, element: HTMLElement, mediaType: number, local: boolean = false) {
        const session: any = this.xmppService.connection.jingle.sessions[call.id];
        let mediaStream;
        //find the right stream to add
        if (local) {
            call.localStreams.forEach((stream: MediaStream) => {
                if (stream.getVideoTracks().length && stream.getAudioTracks().length && mediaType === CallMedia.VIDEO) {
                    mediaStream = stream;
                }
                //if sharing track
                else if (stream.getVideoTracks().length && !stream.getAudioTracks().length && mediaType === CallMedia.SHARING) {
                    mediaStream = stream;
                }
            });
        }
        else {
            if (mediaType === CallMedia.VIDEO) { mediaStream = session?.remoteStreamsObject["1"]; }
            if (mediaType === CallMedia.SHARING) { mediaStream = session?.remoteStreamsObject["2"]; }
        }

        if (!element || !mediaStream) {
            throw new Error(`[CallService] attachStreamToHtmlElement -- missing element or media stream not found`);
        }

        this.webrtcConfigAndUtils.RTC.attachMediaStreamIfNeeded([element], mediaStream, mediaStream.id);
    }

    /**
     * Call REFER management
     * Private APIs related to the webrtc transfer calls / transfer PBX to webrtc / ad-hoc conferences
     */

    //Management of REFER message
    private async onMessageReceived(stanza) {
        try {
            const stanzaElem = xmlParser(stanza);
            const isRefer = stanzaElem.find("refer");
            const isError = stanzaElem.find("error");

            //check if error response to IQ
            if (isError?.length) {
                this.logger.info("[CallService] onMessageReceived -- error stanza received, do nothing");
                return;
            }

            // handle internally message refer without IQ
            if (isRefer) {
                const referElem = stanzaElem.find("refer");
                const confEndpointId = referElem.attr("confEndpointId");
                const referTo = referElem.attr("referTo");
                const status = referElem.attr("status");
                const from = stanzaElem.attr("from");//the originator jid
                const telCallId = referElem.attr("telCallId") ? referElem.attr("telCallId") : "";

                this.logger.info(`[CallService] onMessageReceived -- refer: confEndpointId ${confEndpointId}, referTo ${referTo}, status ${status}, from ${from}, telCallId ${telCallId}`);

                let myTelCall = null;
                let possibleCall = null;
                const shouldMute = false;
                const mediaPillarCalls = this.webrtcP2PService.getMediaPillarAudioCalls();

                if (telCallId) {
                    if (this.telephonyService.isOfficeMode()) {
                        //user is using office phone, manage the call by his id
                        myTelCall = this.telephonyService.calls[telCallId];
                    }
                    else {
                        possibleCall = this.telephonyService.calls[telCallId];
                        if (mediaPillarCalls?.length && possibleCall) {
                            myTelCall = this.telephonyService.calls[telCallId];
                        } else {
                            this.logger.info("[CallService] onMessageReceived -- refer: not my callId, ignore");
                            return;
                        }
                    }

                } else {
                    //if no call Id provided then infers from available info
                    //check if we've a current MP call with the same contact from who we receive the refer message;
                    possibleCall = (mediaPillarCalls?.length) ? mediaPillarCalls[0] : undefined;
                    if (possibleCall?.contact && possibleCall.contact.jid === this.xmppService.getBareJidFromJid(from)) {
                        myTelCall = this.telephonyService.getActiveCall();
                    } else {
                        this.logger.info("[CallService] onMessageReceived : Not my call then Ignore");
                        return;
                    }
                }

                if (confEndpointId && referTo && status !== 'complete') {
                    //receiving a refer to join SFU
                    if (!this.bubbleService) throw new Error(`[CallService] onMessageReceived refer -- bubble plugin is missing`);
                    if (!this.webConferenceService) throw new Error(`[CallService]  onMessageReceived refer -- bubble conference plugin is missing`);

                    let bubble = this.bubbleService.getBubbleFromCache(confEndpointId);
                    //wait for the room to be created, but it's better to use the event from the conversationService and use the DB ID
                    if (!bubble) {
                        try { bubble = await this.bubbleService.getServerBubble(confEndpointId); }
                        catch { this.logger.warn("[CallService] | adhoc conference v1 type"); }
                    }

                    if (bubble?.isBubbleConferenceStarted()) {
                        bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
                        await this.webConferenceService.transferP2pCallToConference(null, referTo, bubble, false, shouldMute);
                        const id = "web_" + crypto.randomUUID();
                        this.xmppService.send($msg({ from: this.contactService.userContact.fullJid, to: from, id })
                            .c('refer', {
                                xmlns: 'urn:xmpp:jingle:apps:rtp:info:1',
                                status: 'complete',
                                telCallId: telCallId
                            }));
                        this.conversationService?.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED, bubble);
                        return;
                    }

                } else if (status === 'complete') {
                    //receive refer complete
                    this.logger.info("[CallService] onMessageReceived : refer complete received");
                    if (myTelCall && (myTelCall.callStatus === CallStatus.ACTIVE || myTelCall.callStatus === CallStatus.HOLD)) {
                        //former tel call to release
                        this.logger.info(`[CallService] onMessageReceived : callId ${telCallId} to release`);
                        this.telephonyService.releaseCall(myTelCall);
                    }
                }
            }
        }
        catch (error) {
            this.logger.error(`[CallService] onMessageReceived -- error ${error}`);
        }
    }

    private async onJingleSessionRefer(sid: string, status: string, confEndpointId: string, referTo: string, method: string, otherSid: string) {
        this.logger.info(`[CallService] onJingleSessionRefer -- refer: ${sid} - status: ${status} - confEndpointId: ${confEndpointId} - referTo: ${referTo} - method: ${method}`);
        const session = this.xmppService.connection.jingle.sessions[sid];
        if (session.isWebinarSession) return;

        if (status === "complete") {
            const call = this.webrtcP2PService?.calls[sid];
            this.webrtcP2PService.onCallTransferred(call);
        }
        else if (session) {
            const call = this.webrtcP2PService?.calls[sid];
            if (confEndpointId) {
                const shouldMute = (call?.isMuted);

                if (!this.bubbleService) throw new Error(`[CallService] onJingleSessionRefer -- bubble plugin is missing`);
                if (!this.webConferenceService) throw new Error(`[CallService]  onJingleSessionRefer -- bubble conference plugin is missing`);

                let bubble = this.bubbleService.getBubbleFromCache(confEndpointId);
                //wait for the room to be created, but it's better to use the event from the conversationService and use the DB ID
                if (!bubble?.isBubbleConferenceStarted()) {
                    try {
                        bubble = await this.bubbleService.getServerBubble(confEndpointId);
                    }
                    catch {
                        this.logger.warn("[CallService] onJingleSessionRefer -- adhoc conference v1 type");
                    }
                }

                if (bubble?.isBubbleConferenceStarted()) {
                    bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
                    await this.webConferenceService.transferP2pCallToConference(session, referTo, bubble, false, shouldMute);
                    this.conversationService?.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED, bubble);
                    this.webrtcP2PService.sendReferStatus(sid, "complete");
                    return;
                }

            }
            else if (referTo && otherSid) {
                this.logger.debug(`[CallService] jingleSessionInfoRefer -- transfer call is requested for session ${sid} to contact ${referTo}`);
                //we need to make initial call to that user and send him the otherSid parameter
                await this.webrtcP2PService.webrtcP2PEventHandler.onTrasferCallEvent(referTo, otherSid);
                this.webrtcP2PService.onCallTransferred(call);
                this.webrtcP2PService.sendReferStatus(sid, "complete");
            } else if (method && referTo) {
                this.onCallBlindTransfer(call, { method: method, referTo: referTo });
            } else {
                this.logger.error("[CallService] jingleSessionInfoRefer -- wrong parameters ");
            }
        }
        else {
            this.logger.error("[CallService] jingleSessionInfoRefer -- no session ");
        }
    }

    private onCallBlindTransfer(call, transferData) {
        this.logger.info(`[CallService] onCallBlindTransfer for call ${call.id} and data ${transferData?.method} ${transferData.referTo}`);
        this.webrtcP2PService.releaseCall(call);

        if (transferData.method === "webrtc") {
            const contact = this.contactService.getContactFromCacheByJid(transferData.referTo);
            if (!contact) { this.logger.error(`[CallService] onCallBlindTransfer for call ${call.id} error -- contact not found`); return; }
            this.webrtcP2PService.makeCall(contact, "audio");
        }
        else if (transferData.method === "pbx") {
            this.telephonyService.makeCallByPhoneNumber(transferData.referTo);
        }
        else {
            this.logger.error("[CallService] onCallBlindTransfer error - wrong transfer data");
        }
    }

    private jingleP2POnSetLocalDescription(sid: string) {
        const sess = this.xmppService.connection.jingle.sessions[sid];
        if (sess?.enableDSCP && !sess?.isWebinarSession) {
            //we need to check if we need to update the session DSCP
            this.logger.info("[CallService] jingleP2POnSetLocalDescription for session " + sid);
            sess.setDSCPValues();
        }
    }

    private jingleP2PUpdateStream(sid: string) {
        const call = this.webrtcP2PService?.calls[sid];
        const sess = this.xmppService.connection.jingle.sessions[sid];
        if (call && sess && !sess.isWebinarSession && !this.webConferenceService?.hasActiveWebConferenceSession()) {
            this.logger.info("[CallService] jingleP2PUpdateStream for session " + sid);
            this.webrtcP2PService.attachDistantMediaStreams(true, call);
        }
    }

}
